import React, { useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { CheckCircle2, Circle, HelpCircle } from 'lucide-react';

const Tooltip = ({ text }) => {
    const [show, setShow] = useState(false);
    return (
        <span className="relative inline-block ml-1.5 align-middle">
            <button
                onClick={() => setShow(!show)}
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
                className="text-[#F0EFF4]/30 hover:text-[#7B61FF] transition-colors"
                aria-label="More info"
            >
                <HelpCircle className="w-3.5 h-3.5" />
            </button>
            {show && (
                <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-[#18181B] border border-[#F0EFF4]/20 rounded-xl text-xs text-[#F0EFF4]/80 leading-relaxed shadow-xl">
                    {text}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#18181B]" />
                </div>
            )}
        </span>
    );
};

const steps = [
    {
        id: 'intake',
        progressKey: 'intake_complete',
        title: 'Tell Us About Your Business',
        subtitle: null,
        description: 'Fill out a quick form so we can design your agent\'s personality, priorities, and workflow.',
        tooltip: 'This form captures your business info, tools you use, and what you want your AI agent to focus on. Adam will review it with you on your pre-consult call.',
        path: 'intake',
        ctaLabel: 'Start Intake Form →'
    },
    {
        id: 'agent_email',
        progressKey: 'agent_email_ready',
        title: 'Create Your Agent\'s Email',
        subtitle: 'You set this up at your domain',
        description: 'Pick a name for your agent (e.g., Jamie Boss) and create an email at your domain (e.g., jamie@yourcompany.com).',
        tooltip: 'Your agent needs its own professional email — separate from yours. It will use this to send estimates, follow-ups, and handle communication on your behalf. We\'ll use this email to set up the Apple ID and other accounts.',
        path: null,
        ctaLabel: null
    },
    {
        id: 'credentials',
        progressKey: 'credentials_submitted',
        title: 'Share Your Tool Credentials',
        subtitle: 'Securely via Password Pusher',
        description: 'Share API keys or logins for the tools you want your agent connected to (CRM, calendar, phone, etc.).',
        tooltip: 'The more tools your agent can access, the more it can do without you. Start with your top 3 — you can always add more later. We use Password Pusher for secure credential transfer.',
        path: 'credentials',
        ctaLabel: 'Share Credentials →'
    },
    {
        id: 'tailscale',
        progressKey: 'tailscale_ready',
        title: 'Create a Tailscale Account',
        subtitle: 'Free, 5 minutes',
        description: 'Sign up at tailscale.com as yourself. You\'ll be the admin of your secure network.',
        tooltip: 'Tailscale creates a private, encrypted network between your devices. Your agent\'s machine joins YOUR network — you stay in control as the admin. It\'s like a VPN but simpler. Free for personal use.',
        path: 'tailscale',
        ctaLabel: 'Set Up Tailscale →'
    },
    {
        id: 'apple_id',
        progressKey: 'apple_id_ready',
        title: 'Apple ID',
        subtitle: 'We handle this — no action needed',
        description: 'We\'ll create an Apple ID using your agent\'s email and set up two-factor authentication. We transfer it to you after delivery.',
        tooltip: 'The Apple ID is needed for the Mac mini your agent runs on. We set it up using the agent email you created, configure security, and hand it over to you when your agent is delivered.',
        path: null,
        ctaLabel: null
    }
];

const STAGES = [
    'Getting Started',
    'Info Received',
    "We're Building",
    'Almost Ready',
    "You're Live 🎉"
];

const getActiveStageIndex = (progress, stageStr) => {
    if (stageStr === 'Complete' || stageStr === 'Live' || stageStr === 'Delivered') return 4;
    if (stageStr === 'Ready for Delivery') return 3;
    if (progress?.intake_complete && progress?.credentials_submitted) return 2;
    if (progress?.intake_complete) return 1;
    return 0;
};

const OnboardHub = () => {
    const { clientData, token } = useOutletContext();

    // Support both new progress object and fallback to completed_steps array parsing if needed
    const progress = clientData?.progress || {
        intake_complete: clientData?.completed_steps?.includes('intake'),
        apple_id_ready: clientData?.completed_steps?.includes('apple_id'),
        agent_email_ready: clientData?.completed_steps?.includes('agent_email'),
        credentials_submitted: clientData?.completed_steps?.includes('credentials'),
        tailscale_ready: clientData?.completed_steps?.includes('tailscale')
    };

    const activeStageIndex = getActiveStageIndex(progress, clientData?.stage);
    const completedCount = steps.filter(s => progress[s.progressKey]).length;
    const allComplete = completedCount === steps.length;

    // Find the first incomplete step
    const nextStep = steps.find(s => !progress[s.progressKey]);

    const firstName = (clientData?.name || clientData?.client_name || 'there').split(' ')[0];
    const company = clientData?.company || 'your company';

    return (
        <div className="animate-fade-in">
            {/* Header */}
            <header className="mb-12">
                <h1 className="text-4xl font-bold tracking-tight mb-2">
                    Welcome, {firstName} 👋
                </h1>
                <p className="text-[#F0EFF4]/60 text-lg mb-1">
                    Here's what we need to get {company} up and running.
                </p>
                <p className="text-[#F0EFF4]/40 text-sm">
                    Most clients are live within 5–7 business days.
                </p>
            </header>

            {/* Pipeline Status */}
            <div className="bg-[#18181B] border border-[#7B61FF]/30 rounded-2xl p-6 mb-12">
                <div className="mb-6">
                    <h3 className="text-[#F0EFF4]/60 text-sm font-data uppercase tracking-wider">
                        Your Setup Progress
                    </h3>
                </div>

                {/* Horizontal Pipeline */}
                <div className="relative">
                    {/* Connecting Line background */}
                    <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-[#F0EFF4]/10 -translate-y-1/2 hidden md:block" />
                    {/* Active connecting line */}
                    <div
                        className="absolute top-1/2 left-0 h-0.5 bg-[#7B61FF] -translate-y-1/2 hidden md:block transition-all duration-500 ease-in-out"
                        style={{ width: `${(activeStageIndex / (STAGES.length - 1)) * 100}%` }}
                    />

                    <div className="flex flex-col md:flex-row justify-between gap-4 md:gap-0 relative z-10">
                        {STAGES.map((stage, idx) => {
                            const isCompleted = idx < activeStageIndex;
                            const isActive = idx === activeStageIndex;
                            const isFuture = idx > activeStageIndex;

                            // Hide first stage on small screens if we've moved past it
                            if (idx === 0 && activeStageIndex > 0) return null;

                            return (
                                <div
                                    key={stage}
                                    className={`flex md:flex-col items-center gap-3 md:gap-2 transition-opacity duration-300 ${isFuture ? 'opacity-40' : 'opacity-100'}`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${
                                        isActive
                                            ? 'bg-[#7B61FF] border-[#7B61FF] text-white shadow-[0_0_15px_rgba(123,97,255,0.4)]'
                                            : isCompleted
                                            ? 'bg-[#10B981] border-[#10B981] text-white'
                                            : 'bg-[#18181B] border-[#F0EFF4]/20 text-[#F0EFF4]/20'
                                    }`}>
                                        {isCompleted
                                            ? <CheckCircle2 className="w-5 h-5" />
                                            : <Circle className="w-3 h-3 fill-current" />
                                        }
                                    </div>
                                    <span className={`text-sm font-bold ${
                                        isActive ? 'text-[#7B61FF]' : isCompleted ? 'text-[#F0EFF4]' : 'text-[#F0EFF4]/60'
                                    }`}>
                                        {stage}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* All-complete state */}
            {allComplete ? (
                <div className="mt-4 p-8 border border-[#10B981]/30 bg-[#10B981]/5 rounded-3xl text-center">
                    <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">You're all set! 🎉</h3>
                    <p className="text-[#F0EFF4]/70">
                        Our team is building your system now. We'll reach out via your preferred channel when it's ready. Average build time: 5–7 business days.
                    </p>
                </div>
            ) : (
                <>
                    {/* Hero Card — Next Step */}
                    {nextStep && (
                        <div className="mb-8 p-6 rounded-2xl border border-[#7B61FF]/50 bg-[#7B61FF]/10 shadow-[0_0_30px_rgba(123,97,255,0.15)]">
                            <p className="text-[#7B61FF] text-xs font-data uppercase tracking-widest mb-2">Next Step</p>
                            <h2 className="text-2xl font-bold text-[#F0EFF4] mb-2">
                                {nextStep.title}
                                {nextStep.tooltip && <Tooltip text={nextStep.tooltip} />}
                            </h2>
                            <p className="text-[#F0EFF4]/70 mb-5">{nextStep.description}</p>
                            {nextStep.path ? (
                                <Link
                                    to={`/onboard/${token}/${nextStep.path}`}
                                    className="inline-block px-6 py-3 bg-[#7B61FF] text-[#F0EFF4] font-bold rounded-full hover:bg-[#6B51EF] transition-colors text-sm"
                                >
                                    {nextStep.ctaLabel}
                                </Link>
                            ) : nextStep.id === 'agent_email' ? (
                                <div className="space-y-3">
                                    <p className="text-[#F0EFF4]/50 text-sm">
                                        Once you've created the email, share the credentials with us using the button below.
                                    </p>
                                    <Link
                                        to={`/onboard/${token}/credentials`}
                                        className="inline-block px-6 py-3 bg-[#7B61FF] text-[#F0EFF4] font-bold rounded-full hover:bg-[#6B51EF] transition-colors text-sm"
                                    >
                                        Share Agent Email Credentials →
                                    </Link>
                                </div>
                            ) : (
                                <p className="text-[#F0EFF4]/50 text-sm italic">
                                    {nextStep.subtitle || "We'll handle this step for you."}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Step Checklist */}
                    <div className="space-y-3">
                        {steps.map((step, index) => {
                            const isComplete = progress[step.progressKey];
                            const isNext = step.id === nextStep?.id;
                            const isFuture = !isComplete && !isNext;
                            const handledByUs = !step.path;

                            return (
                                <div
                                    key={step.id}
                                    className={`flex items-center gap-4 px-5 py-4 rounded-2xl border transition-all ${
                                        isComplete
                                            ? 'bg-[#10B981]/5 border-[#10B981]/20'
                                            : isNext
                                            ? 'bg-[#7B61FF]/5 border-[#7B61FF]/20'
                                            : 'border-transparent opacity-50'
                                    }`}
                                >
                                    {/* Icon / Step number */}
                                    <div className="shrink-0">
                                        {isComplete ? (
                                            <div className="w-7 h-7 rounded-full bg-[#10B981] flex items-center justify-center text-white">
                                                <CheckCircle2 className="w-4 h-4" />
                                            </div>
                                        ) : (
                                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${
                                                isNext
                                                    ? 'border-[#7B61FF] text-[#7B61FF]'
                                                    : 'border-[#F0EFF4]/20 text-[#F0EFF4]/30'
                                            }`}>
                                                {index + 1}
                                            </div>
                                        )}
                                    </div>

                                    {/* Title + subtitle */}
                                    <div className="flex-grow min-w-0">
                                        <span className={`font-bold block ${
                                            isComplete ? 'text-[#F0EFF4]/50 line-through' : 'text-[#F0EFF4]'
                                        }`}>
                                            {step.title}
                                            {!isComplete && step.tooltip && <Tooltip text={step.tooltip} />}
                                        </span>
                                        {step.subtitle && !isComplete && (
                                            <span className="text-xs text-[#F0EFF4]/40 mt-0.5 block">
                                                {step.subtitle}
                                            </span>
                                        )}
                                        {isFuture && handledByUs && (
                                            <span className="text-xs text-[#F0EFF4]/40 mt-0.5 block italic">
                                                We'll handle this step — you'll hear from us.
                                            </span>
                                        )}
                                    </div>

                                    {/* Status badge */}
                                    <div className="shrink-0">
                                        {isComplete && (
                                            <span className="px-3 py-1 bg-[#10B981]/10 text-[#10B981] font-bold text-xs rounded-full">
                                                Done ✓
                                            </span>
                                        )}
                                        {isNext && (
                                            <span className="px-3 py-1 bg-[#7B61FF]/10 text-[#7B61FF] font-bold text-xs rounded-full">
                                                Up Next
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default OnboardHub;
